import { TextField } from "@authentic/loyalty-pwa/components/Form/TextField";
import { ToggleButton } from "@authentic/loyalty-pwa/components/Form/ToggleButton";
import { link, route, Typography } from "@authentic/loyalty-pwa/util";
import { Box } from "@authentic/loyalty-pwa/util/Box";
import { FunctionalComponent, h } from "preact";
import { Controller, useForm } from "react-hook-form";
import { DelYeahContainer } from "../../../../containers/DelYeahContainer";
import { BackIcon } from "../../../../icons/Back";
import { Location } from '../../../../components/Location';
import { EditProfileContentStyled, EditProfilePageStyled, FormControlStyled, FormHint, FormStyled, FormTitleStyled, InputErrorStyled } from "./edit-profile.style";
import { useEffect, useState, useContext } from "preact/hooks";
import { UserProvider, UserServiceContext, useUser } from "@authentic/loyalty-pwa/providers";
import { ProfileUpdateResult } from "@authentic/loyalty-pwa/interfaces";
import { Snackbar, SnackbarType } from "../../../../components/snackbar/snackbar";
import { ErrorMessage } from "../../../../components/Error";
import { ErrorIcon } from "@authentic/loyalty-pwa/icons";
import { objectIsEmpty } from "@authentic/loyalty-pwa/util/helpers";

export const DelYeahEditProfilePage: FunctionalComponent = (props) => {
  const title = 'Edit my Profile';
  const htmlTitle: string = 'Edit Del Yeah! Rewards Profile';
  const htmlMetaDescription: string = 'Your Del Yeah! Rewards Dashboard of available rewards and offers.';
  const IDItemMenuSelected: string = '7';
  const successfulMessage: string = 'Success!'; // according to what is specified in ticket DTLA-91
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState(SnackbarType.Success);

  const user = useUser();
  const userService = useContext(UserServiceContext);
  const [locationErrorMessage, setLocationErrorMessage] = useState('');
  const [receiveEmail, setReceiveEmail] = useState(false);
  const [receiveTexts, setReceiveTexts] = useState(false);

  const [locationLabelValue, setLocationLabelValue] = useState(
    `${user.favorite_location?.city} ${user.favorite_location?.address} ${user.favorite_location?.state}`
  );
  const [defaultFormValues, setDefaultFormValues] = useState({
    firstName: '',
    lastName: '',
    mobile: '',
    receiveEmail: false,
    receiveTexts: false,
    location: -1,
    birthdate: ''
  });

  const {
    setError,
    setValue,
    handleSubmit,
    getValues,
    control,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      ...defaultFormValues
    }
  });

  useEffect(() => {
    if (user.profile) {
      setUserProfileInfoIntoForm();
    }
  }, [user]);

  const resetUserProfileData = () => {
    UserProvider.get().summaryReset();
  };

  const updateUserProfileInfo = (data: any) => {
    console.log('data.location: ', data.location);
    if (data.location > -1) {
      setLocationErrorMessage('');
      let optionalFields = {};
      if (data.birthdate) {
        optionalFields = {
          birthdate: parseBirthDateToSendAPI(data.birthdate)
        }
      }
      userService?.updateProfile({
        first_name: data.firstName,
        last_name: data.lastName,
        favorite_location: data.location,
        mobile_phone: data.mobile,
        receive_mail_offers: data.receiveEmail,
        receive_sms_offers: data.receiveTexts,
        ...optionalFields
      }).then((res: ProfileUpdateResult) => {
        console.log(res);
        if (res.success) {
          resetUserProfileData();
          setSnackbarType(SnackbarType.Success);
          //TODO: define the correct successful message 'cause the api doesn't respond a message.
          //we've defined a custom message but it should be defined in the ticket.
          setSnackbarMessage(successfulMessage);
          setOpenSnackbar(true);
        } else {
          // case: mobile phone error (the api is not specific with the error type), for this case the api respond like this:
          // {"code":4400,"name":"invalid_record","message":"Mobile Phone is not valid. Please enter a valid phone number. Example: 917 541 1211"}
          if (res.name === 'invalid_record' && res.code === 4400 && res.message && res.message.indexOf('Mobile') > -1) {
            setError('mobile', { message: res.message }, { shouldFocus: true });
          }
        }
      });
    } else {
      setLocationErrorMessage(`Favorite Location can't be blank.`);
      setError('location', { message: 'something' }, { shouldFocus: true });
    }
  };

  const setUserProfileInfoIntoForm = () => {
    console.log('user.profile?.: ', user.profile);

    if (user.profile?.first_name) {
      setValue('firstName', user.profile.first_name);
    }
    if (user.profile?.last_name) {
      setValue('lastName', user.profile.last_name);
    }

    if (user.profile?.mobile_phone) {
      setValue('mobile', user.profile.mobile_phone.substring(2));
    }

    if (user.profile?.receive_mail_offers) {
      setValue('receiveEmail', !!user.profile.receive_mail_offers); onChangeReceiveEmail();
    }

    if (user.profile?.receive_sms_offers) {
      setValue('receiveTexts', !!user.profile.receive_sms_offers); onChangeReceiveTexts();
    }

    if (user.profile?.birthdate) { // format birthdate: YYYY-MM-DD
      setValue('birthdate', parseBirthDateToInputMask(user.profile.birthdate));
    }

    if (user.profile?.favorite_location) {
      setValue('location', parseInt(user.profile.favorite_location));
    }
  }

  const back = () => {
    route('/profile');
  }

  const onSubmit = (data: any) => {
    console.log('form data: ', data);
    updateUserProfileInfo(data);
  };

  const onChangeReceiveEmail = () => {
    setTimeout(() => {
      setReceiveEmail(getValues('receiveEmail'));
      setDefaultFormValues((prevState: any) => ({
        ...prevState,
        receiveEmail: getValues('receiveEmail'),
      }));
    }, 0);
  };

  const onChangeReceiveTexts = () => {
    setTimeout(() => {
      setReceiveTexts(getValues('receiveTexts'));
      setDefaultFormValues((prevState: any) => ({
        ...prevState,
        receiveTexts: getValues('receiveTexts'),
      }));
    }, 0);
  };

  const onSelectLocation = (data: any) => {
    setValue('location', data?.integration_id);
    setLocationLabelValue(data?.labelValue);
  };

  // parse from MM/DD/YYYY to YYYY-MM-DD
  const parseBirthDateToSendAPI = (birthdate: string) => {
    const dateItems = birthdate.split('/');
    return `${dateItems[2]}-${dateItems[0]}-${dateItems[1]}`;
  };

  // parse from YYYY-MM-DD to MM/DD/YYYY
  const parseBirthDateToInputMask = (birthdate: string) => {
    const dateItems = birthdate.split('-');
    return `${dateItems[1]}/${dateItems[2]}/${dateItems[0]}`;
  };

  const callbackOnCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const onChangeLocationCallback = (wordSearched: string) => {
    if (!wordSearched) {
      setValue('location', -1);
    }
  }

  return (
    <DelYeahContainer htmlTitle={htmlTitle} htmlMetaDescription={htmlMetaDescription} IDItemMenuSelected={IDItemMenuSelected}>
      <EditProfilePageStyled>

        <FormStyled onSubmit={handleSubmit(onSubmit)}>
          <FormTitleStyled>
            <div onClick={back}>
              <BackIcon></BackIcon>
            </div>
            <span>{title}</span>
            <button role="button" type="submit" class="link">Save</button>
          </FormTitleStyled>

          <EditProfileContentStyled>
            <FormHint>*Required Field</FormHint>

            {!objectIsEmpty(errors) && (
              <ErrorMessage>Review highlighted errors to continue</ErrorMessage>
            )}

            <Box mb={24} mt={32}>
              <Typography variant="headline3">My Information</Typography>
            </Box>
            <FormControlStyled>
              <Controller
                render={({ field }: any): any => (
                  <TextField
                    error={errors?.firstName}
                    label="First Name"
                    required
                    {...field}
                    id="firstName"
                  />
                )}
                rules={{
                  required: {
                    value: true,
                    message: 'Please enter your First Name',
                  },
                }}
                control={control}
                name="firstName"
                defaultValue=""
              />
            </FormControlStyled>

            <FormControlStyled>
              <Controller
                render={({ field }: any): any => (
                  <TextField
                    error={errors?.lastName}
                    label="Last Name"
                    {...field}
                    required
                    id="lastName"
                  />
                )}
                rules={{
                  required: {
                    value: true,
                    message: 'Please enter your Last Name',
                  },
                }}
                control={control}
                name="lastName"
                defaultValue=""
              />
            </FormControlStyled>

            <FormControlStyled>
              <Controller
                render={({ field }: any): any => (
                  <TextField
                    error={errors?.mobile}
                    mask="(999) 999-9999"
                    maskPlaceholder="(   )    -    "
                    label="Mobile"
                    type="tel"
                    required
                    {...field}
                    id="mobile"
                  />
                )}
                rules={{
                  required: {
                    value: true,
                    message: 'Please enter your mobile',
                  },
                }}
                control={control}
                name="mobile"
                defaultValue=""
              />
            </FormControlStyled>

            <FormControlStyled>
              <Controller
                render={({ field }: any): any => (
                  <TextField
                    mask="99/99/9999"
                    maskPlaceholder="mm/dd/yyyy"
                    label="Birthday"
                    {...field}
                    error={errors?.birthdate}
                    id="birthdate"
                  />
                )}
                control={control}
                name="birthdate"
                rules={{
                  pattern: {
                    value: /^(0?[1-9]|1[0-2])\/(0?[1-9]|1\d|2\d|3[01])\/(19|20)\d{2}$/,
                    message: 'Enter a valid date (mm/dd/yyyy)',
                  },
                }}
                defaultValue=""
              />
            </FormControlStyled>

            <Box mb={24} mt={32}>
              <Typography variant="headline3">My Preferences</Typography>
            </Box>

            <FormControlStyled>
              <Controller
                render={({ field }: any): any => (
                  <ToggleButton
                    error={errors?.receiveEmail}
                    label="Receive emails from Del Taco"
                    required
                    callback={onChangeReceiveEmail}
                    checked={receiveEmail}
                    {...field}
                    id="receiveEmail"
                  >
                    <Typography variant="body1">Receive emails from Del Taco</Typography>
                  </ToggleButton>
                )}
                control={control}
                name="receiveEmail"
                defaultValue={receiveEmail}
              />
            </FormControlStyled>

            <FormControlStyled>
              <Controller
                render={({ field }: any): any => (
                  <ToggleButton
                    error={errors?.receiveTexts}
                    label="Receive emails from Del Taco"
                    required
                    callback={onChangeReceiveTexts}
                    checked={receiveTexts}
                    {...field}
                    id="receiveTexts"
                  >
                    <div>
                      <Box mb={8}>
                        <Typography variant="body1">Receive texts from Del Taco</Typography>
                      </Box>
                      <Typography variant="body3">
                        Message and data rates may apply. <br /> Recurring messages will be sent.
                      </Typography>
                      <Typography variant="body3">
                        <a
                          href={link('/terms')}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Terms & Conditions
                        </a>
                        &nbsp;|&nbsp;
                        <a href={link('/privacy')} target="_blank" rel="noopener noreferrer">
                          Privacy Policy
                        </a>
                      </Typography>
                    </div>
                  </ToggleButton>
                )}
                control={control}
                name="receiveTexts"
                defaultValue={receiveTexts}
              />
            </FormControlStyled>

            <Box mb={12} mt={32}>
              <Typography variant="headline3">Favorite Location</Typography>
            </Box>

            <FormControlStyled>
              <Location
                name="location"
                callback={onSelectLocation}
                defaultLabelValue={locationLabelValue}
                onChangeCallback={onChangeLocationCallback} />
              <InputErrorStyled>{locationErrorMessage && <span role="alert">{locationErrorMessage}</span>}</InputErrorStyled>
            </FormControlStyled>
          </EditProfileContentStyled>
        </FormStyled>

      </EditProfilePageStyled>

      <Snackbar
        message={snackbarMessage}
        isActive={openSnackbar}
        type={snackbarType}
        timeout={10000}
        callbackOnClose={callbackOnCloseSnackbar}></Snackbar>
    </DelYeahContainer>
  )
}
